/**
 * Agent of Agents — pure timeline reducer.
 *
 * Folds the SSE event stream into an ordered TimelineItem list that the
 * UI renders top-to-bottom. Unit-testable: no React, no I/O.
 */

import type { AoAAttachment, AoAMessage, AoAProposal, StreamEvent, TextItem, TimelineItem, ToolCallItem } from './types';
import type { WebSource } from '@/types/models';
import { answeredByOf } from '@/utils/modelLabels';
import type { StoppedTurn } from '@/utils/stopReconcile';

export interface TimelineState {
  items: TimelineItem[];
  /** True from the first non-done event until 'done' arrives. */
  streaming: boolean;
  /** Coarse activity status driven by the latest event class. Used to
   *  render a single bottom-left indicator above the input bar — never
   *  duplicated inside the timeline. `null` when idle. */
  activity: 'thinking' | 'tool' | 'typing' | null;
  /** Name of the tool currently in flight (when activity === 'tool'). */
  activeTool?: string | null;
  /** Finer live status inside the current tool round ("Writing the
   *  document… N words"), cleared by the next event class. */
  progress?: { key: string; tool?: string; params?: Record<string, unknown> } | null;
}

export function emptyState(): TimelineState {
  return { items: [], streaming: false, activity: null, activeTool: null, progress: null };
}

/**
 * The backend fires `skill_activation` for TOOL activations as well as real
 * skills, so a raw banner reads "Using skill: read_file" — which is not a
 * skill, and sits redundantly under the tool-call line for the same call.
 * Platform skills are human-named ("Agent Evaluator"); tools are snake_case.
 * Announce only the former.
 *
 * Shared by the LIVE `skill_activation` case and by `hydrateFromMessages`, so
 * a reloaded conversation can never show a badge the live view suppressed.
 * The backend keeps a byte-identical copy (`_looks_like_tool_name` in
 * `backend/modules/agent_of_agents/api/routes.py`) to avoid persisting them at
 * all; the parity is pinned by a unit test.
 */
export function looksLikeToolName(name: string | undefined | null): boolean {
  return /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/.test(name || '');
}

/** Which release answered a stored message, as a text item records it. */
function answerLabelOf(message: { message_metadata?: unknown } | undefined): Pick<TextItem, 'model' | 'answeredByName' | 'servedByFallback'> {
  const answered = answeredByOf(message);
  return {
    model: ((message?.message_metadata as Record<string, unknown> | undefined)?.model as string | undefined) ?? null,
    answeredByName: answered?.name ?? null,
    servedByFallback: !!answered?.served_by_fallback,
  };
}

/**
 * What a stopped turn's segments take from its stored row once the re-read
 * finds it: the release that served it (every segment, as `ai_complete`
 * does) and, under the last segment, its Sources and its stored id. Never
 * text. A row with no model recorded (older rows) leaves the label as it is.
 */
function withStoredAnswer(
  items: TimelineItem[], segments: readonly number[], message: AoAMessage | undefined,
): TimelineItem[] {
  if (!message || segments.length === 0) return items;
  const meta = (message.message_metadata ?? {}) as Record<string, unknown>;
  const label = answerLabelOf(message);
  const last = segments[segments.length - 1];
  return items.map((it, i) => {
    if (!segments.includes(i) || it.kind !== 'text') return it;
    const labelled: TextItem = label.model ? { ...it, ...label } : it;
    return i === last
      ? { ...labelled, messageId: message.message_id,
        webSources: (meta.web_sources as WebSource[] | undefined) ?? it.webSources }
      : labelled;
  });
}

/**
 * The stored row a Stop of the turn on screen will be read back from: the
 * answer a Continue was growing, or the row after the turn's own (confirmed)
 * user message.
 */
export function stoppedTurnOf(items: readonly TimelineItem[]): StoppedTurn {
  for (const it of items) {
    if (it.kind === 'text' && it.role === 'assistant' && it.streaming && it.continueBase !== undefined) {
      if (it.messageId) return { messageId: it.messageId };
    }
  }
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const it = items[i];
    if (it.kind === 'text' && it.role === 'user') return { afterMessageId: it.messageId };
  }
  return {};
}

/** Whether the turn on screen has shown any answer text: a Stop before the
 *  first word stores no row, so there is nothing to read back. */
export function turnShowsText(items: readonly TimelineItem[]): boolean {
  return items.some((it) => it.kind === 'text' && it.role === 'assistant'
    && (it.streaming === true) && it.content.trim() !== '');
}

/**
 * The stopped turn's assistant segments still marked "Response stopped" —
 * for a Continue, the answer it grew (`turn.messageId`). With the turn's user
 * message (`turn.afterMessageId`), the segments between
 * it and the next user message — the person may already have sent their
 * next one. Without it, walking back from the end, stopping at the user's
 * message OR at an assistant block that is not interrupted — either one is a
 * turn boundary, so an EARLIER stopped turn is never reached.
 */
function stoppedSegments(items: readonly TimelineItem[], turn?: StoppedTurn): number[] {
  if (turn?.messageId) {
    // A stopped Continue: the answer it was growing.
    return items.flatMap((it, i) => (
      it.kind === 'text' && it.role === 'assistant' && it.messageId === turn.messageId ? [i] : []));
  }
  let from = -1;
  let to = items.length;
  const asked = turn?.afterMessageId
    ? items.findIndex((it) => it.kind === 'text' && it.role === 'user' && it.messageId === turn.afterMessageId)
    : -1;
  if (asked >= 0) {
    from = asked;
    const next = items.findIndex((it, i) => i > asked && it.kind === 'text' && it.role === 'user');
    if (next >= 0) to = next;
  } else {
    for (let i = items.length - 1; i >= 0; i -= 1) {
      const it = items[i];
      if (it.kind !== 'text') continue;
      if (it.role === 'user' || !it.interrupted) { from = i; break; }
    }
  }
  const marked: number[] = [];
  for (let i = from + 1; i < to; i += 1) {
    const it = items[i];
    if (it.kind === 'text' && it.role === 'assistant' && it.interrupted) marked.push(i);
  }
  return marked;
}

export function reduceTimeline(state: TimelineState, ev: StreamEvent): TimelineState {
  switch (ev.type) {
    case 'user_message':
      return {
        ...state,
        streaming: true,
        activity: 'thinking',
        activeTool: null,
        items: [
          ...state.items,
          {
            kind: 'text',
            role: 'user',
            content: ev.message.message_content,
            // Carry attachments through so they render above the user
            // bubble — same UX as regular chat. ev.message.attachments
            // comes straight from Message.to_dict() on the backend.
            attachments: (ev.message as any)?.attachments ?? undefined,
            // The stored message: a Stop reads its turn's answer back by it.
            messageId: ev.message.message_id,
          },
        ],
      };

    case 'thinking':
      // Don't add anything to the timeline — just update the activity badge.
      return { ...state, streaming: true, activity: 'thinking', activeTool: null, progress: null };

    case 'progress':
      // Finer status inside the current step — the word counter while a
      // document streams. The badge keeps its tool activity; only the
      // sentence (and its climbing number) changes.
      return {
        ...state,
        streaming: true,
        activity: state.activity === 'typing' ? state.activity : 'tool',
        progress: { key: ev.key, tool: ev.tool, params: ev.detail },
      };

    case 'ai_chunk': {
      // Append to the last assistant text item; if none, start one.
      const items = [...state.items];
      const last = items[items.length - 1];
      if (last && last.kind === 'text' && last.role === 'assistant' && last.streaming) {
        items[items.length - 1] = { ...last, content: last.content + ev.content };
      } else {
        items.push({ kind: 'text', role: 'assistant', content: ev.content, streaming: true });
      }
      return { ...state, items, activity: 'typing', activeTool: null, progress: null };
    }

    case 'ai_complete': {
      // Drop empty streaming assistant text items first — a propose-only
      // AoA turn produces zero prose, and an empty bubble in the chat
      // looks broken. The tool_call + proposal_card items already pushed
      // are the user-visible response in that case.
      const finalText = ev.message?.message_content ?? '';
      if (!finalText.trim()) {
        const items = state.items.filter(
          (it) => !(it.kind === 'text' && it.role === 'assistant' && it.streaming),
        );
        // The output cap ran out before a word of the answer: the cut-off
        // note IS the answer, so it gets a row.
        if ((ev.message as any)?.message_metadata?.answer_cut_off === true) {
          items.push({ kind: 'text', role: 'assistant', content: '', cutOff: true,
                      cutOffReason: (ev.message as any)?.message_metadata?.answer_cut_off_reason });
        }
        return { ...state, items, activity: null, activeTool: null, progress: null };
      }
      // Finalize streaming assistant text blocks.
      //
      // For a SINGLE-segment turn (no tool calls split the prose) we
      // overwrite the one text block's content with the persisted
      // ``message_content`` — that's authoritative if streaming dropped
      // any chunks. But for a MULTI-segment turn (prose → tool → prose →
      // tool → …) the items array looks like:
      //   [text1(streaming), tool1, text2(streaming), tool2, text3(streaming)]
      // and ``message_content`` is the FULL concatenated message. Setting
      // every text segment's content to the full string would duplicate
      // the entire reply once per segment — visible to the user as the
      // same paragraph repeating after each tool call. So in the
      // multi-segment case we keep each segment's STREAMED content
      // (which is already correct) and only flip the streaming flag.
      const label = answerLabelOf(ev.message);
      const streamingTextCount = state.items.reduce(
        (n, it) => (it.kind === 'text' && it.role === 'assistant' && it.streaming ? n + 1 : n),
        0,
      );
      const overwrite = streamingTextCount === 1;
      // The note goes under the LAST segment: that is where the answer stopped.
      const cutOff = (ev.message as any)?.message_metadata?.answer_cut_off === true;
      let lastStreaming = -1;
      state.items.forEach((it, i) => {
        if (it.kind === 'text' && it.role === 'assistant' && it.streaming) lastStreaming = i;
      });
      const items = state.items.map((it, i) => {
        if (!(it.kind === 'text' && it.role === 'assistant' && it.streaming)) return it;
        const stored = ev.message?.message_content;
        // A single segment takes the stored text: the whole message — also
        // after a Continue of a segment that WAS the whole message, so a
        // server-side clean-up of the joined text shows as stored — or, for a
        // Continue of a turn's last segment, what the message grew by.
        let content = it.content;
        if (overwrite && stored !== undefined) {
          const wasWhole = it.continueBase === undefined || it.continueBase.length === it.storedLength;
          content = wasWhole ? stored : (it.continueBase ?? '') + stored.slice(it.storedLength ?? 0);
        }
        const limitReached = (ev.message as any)?.message_metadata?.answer_limit_reached === true;
        const webSources = (ev.message as any)?.message_metadata?.web_sources as WebSource[] | undefined;
        return {
          ...it,
          streaming: false,
          content,
          ...label,
          continueBase: undefined,
          ...(i === lastStreaming
            ? { messageId: ev.message?.message_id, storedLength: (stored ?? '').length, cutOff, limitReached,
                cutOffReason: (ev.message as any)?.message_metadata?.answer_cut_off_reason,
                webSources }
            : {}),
        };
      });
      return { ...state, items, activity: null, activeTool: null, progress: null };
    }

    case 'skill_activation': {
      // Tools arrive on this same channel — see looksLikeToolName above.
      if (looksLikeToolName(ev.skill_name)) return state;
      // Deduped: a skill stays loaded for the rest of the session and
      // re-announces on later turns, which would otherwise stack identical
      // banners down the transcript.
      const already = state.items.some(
        (it) => it.kind === 'activation' && it.label === ev.skill_name,
      );
      if (already) return state;
      return {
        ...state,
        items: [...state.items, { kind: 'activation', label: ev.skill_name }],
      };
    }

    case 'tool_debug': {
      const items = [...state.items];
      if (ev.action === 'call') {
        items.push({
          kind: 'tool_call',
          invocationId: ev.invocation_id || `${ev.tool}-${items.length}`,
          tool: ev.tool,
          args: (ev.args || {}) as Record<string, unknown>,
          status: 'pending',
        });
        return { ...state, items, activity: 'tool', activeTool: ev.tool, progress: null };
      } else if (ev.action === 'result') {
        // Match by invocation_id; fall back to last pending with same tool.
        let target = -1;
        for (let i = items.length - 1; i >= 0; i -= 1) {
          const it = items[i];
          if (it.kind !== 'tool_call') continue;
          if (ev.invocation_id) {
            if (it.invocationId === ev.invocation_id) { target = i; break; }
          } else if (it.tool === ev.tool && it.status === 'pending') {
            target = i;
            break;
          }
        }
        if (target >= 0) {
          const t = items[target] as TimelineItem & { kind: 'tool_call' };
          // Backend sends the tool return value under `preview` (truncated
          // string). Also fall back to `result` for legacy compatibility.
          items[target] = {
            ...t,
            status: 'done',
            result: ev.preview ?? ev.result,
          };
        }
      }
      // After a tool result, the agent will think again before the next step.
      // We don't clear activity here — the next event (thinking / ai_chunk / done)
      // sets the correct state.
      return { ...state, items };
    }

    case 'proposal_emitted': {
      // Defence in depth against a card appearing twice. The backend refuses
      // to create a duplicate pending proposal, but a replayed or re-delivered
      // event would still append a second copy here — and the timeline is
      // append-only, so nothing downstream would collapse them.
      if (state.items.some(
        (it) => it.kind === 'proposal' && it.proposal.proposal_id === ev.proposal_id,
      )) {
        return state;
      }
      const items = [...state.items];
      items.push({
        kind: 'proposal',
        proposal: {
          proposal_id: ev.proposal_id,
          tool_name: ev.tool_name,
          target_entity_type: ev.target_entity_type,
          target_entity_id: ev.target_entity_id,
          field: ev.field,
          old_value: ev.old_value,
          new_value: ev.new_value,
          proposed_payload: (ev.proposed_payload ?? null) as Record<string, unknown> | null,
          edited_payload: null,
          rationale: ev.rationale,
          status: 'pending',
          reject_reason: null,
          worker_hint: ev.worker_hint ?? null,
          created_at: ev.created_at ?? new Date().toISOString(),
          resolved_at: null,
        },
      });
      return { ...state, items };
    }

    case 'proposal_resolved': {
      const items = state.items.map((it) => {
        if (it.kind === 'proposal' && it.proposal.proposal_id === ev.proposal_id) {
          return {
            ...it,
            proposal: { ...it.proposal, status: ev.status as any, resolved_at: new Date().toISOString() },
            resolved: { status: ev.status, resolvedAt: new Date().toISOString() },
          };
        }
        return it;
      });
      return { ...state, items };
    }

    case 'turn_stopped': {
      // User pressed Stop. Freeze whatever the assistant had streamed and
      // mark it, instead of leaving a block that renders forever with a
      // live caret. An empty block is dropped — a stop before the first
      // token should leave no ghost bubble (matches the ai_complete
      // empty-text branch above). Pending tool_call rows are settled too:
      // they would otherwise spin "pending" for the rest of the session.
      const items = state.items
        .filter(
          (it) =>
            !(
              it.kind === 'text' &&
              it.role === 'assistant' &&
              it.streaming &&
              !it.content.trim()
            ),
        )
        .map((it) => {
          if (it.kind === 'text' && it.role === 'assistant' && it.streaming) {
            return { ...it, streaming: false, interrupted: true };
          }
          if (it.kind === 'tool_call' && it.status === 'pending') {
            return { ...it, status: 'done' as const };
          }
          return it;
        });
      return { ...state, items, streaming: false, activity: null, activeTool: null, progress: null };
    }

    case 'continue_start': {
      const it = state.items[ev.index];
      if (!it || it.kind !== 'text' || it.role !== 'assistant') return state;
      const items = [...state.items];
      // `cutOffReason` stays: a Continue that fails offers the answer again
      // with its own note; one that completes refreshes it from the stored message.
      items[ev.index] = { ...it, streaming: true, cutOff: false, continueBase: it.content };
      return { ...state, items, streaming: true, activity: 'thinking', activeTool: null, progress: null };
    }

    case 'continue_failed': {
      const it = state.items[ev.index];
      if (!it || it.kind !== 'text' || !it.streaming) return state;
      const base = it.continueBase ?? it.content;
      const items = [...state.items];
      items[ev.index] = {
        ...it,
        streaming: false,
        cutOff: ev.stillPaused,
        cutOffReason: ev.stillPaused ? it.cutOffReason : undefined,
        continueBase: undefined,
        storedLength: (it.storedLength ?? base.length) + (it.content.length - base.length),
      };
      return { ...state, items, streaming: false, activity: null, activeTool: null, progress: null };
    }

    case 'turn_stop_reconciled': {
      // The Stop landed AFTER the server had already committed this turn's
      // answer (the commit → title/summary → `complete` window). The panel
      // re-read the persisted messages and they say the turn completed, so
      // the optimistic note above was a lie about a finished reply. Undo it.
      //
      // Scope is THIS turn only (`stoppedSegments`): an EARLIER genuinely
      // stopped turn keeps its (correct, persisted) note.
      const markedIdx = stoppedSegments(state.items, ev.turn);
      if (markedIdx.length === 0) return state;
      // Same single-vs-multi-segment rule `ai_complete` uses above: the
      // persisted `message_content` is the WHOLE turn, so writing it into
      // each of a prose→tool→prose turn's segments would repeat the reply
      // once per segment. Multi-segment turns keep their streamed text and
      // only lose the note.
      const overwrite = markedIdx.length === 1;
      const items = withStoredAnswer(state.items, markedIdx, ev.message).map((it, i) => (
        markedIdx.includes(i) && it.kind === 'text'
          ? { ...it, interrupted: false, content: overwrite ? ev.content : it.content } : it));
      return { ...state, items };
    }

    case 'turn_stop_settled': {
      // The turn really was stopped, and its stored row has landed. The
      // frozen text stays exactly as it is — no new text may appear after a
      // Stop — and the row adds what the stream never delivered: the
      // release that served it (every segment, as `ai_complete` does) and,
      // under the last segment, its Sources and its stored id.
      const segments = stoppedSegments(state.items, ev.turn);
      if (segments.length === 0) return state;
      return { ...state, items: withStoredAnswer(state.items, segments, ev.message) };
    }

    case 'compacted': {
      // The cut is the end of the transcript so far: everything before the
      // marker is folded. The turn being sent is NOT part of it — its
      // optimistic user bubble (and the thinking row) is already on screen,
      // so the marker goes in front of that unanswered turn, never after
      // it. An earlier marker is folded into this summary, so it comes off.
      const meta = (ev.summary_message?.message_metadata ?? {}) as Record<string, unknown>;
      const marker: TimelineItem = {
        kind: 'compaction',
        messageId: ev.summary_message?.message_id,
        summary: ev.summary_message?.message_content ?? '',
        summarizedCount: Number(ev.summarized_count ?? meta.summarized_count ?? 0),
        round: Number(ev.round ?? meta.round ?? 1),
      };
      const rest = state.items.filter((it) => it.kind !== 'compaction');
      let at = rest.length;
      for (let i = rest.length - 1; i >= 0; i -= 1) {
        const it = rest[i];
        if (it.kind === 'thinking') continue;
        if (it.kind === 'text' && it.role === 'user') { at = i; }
        break;
      }
      const items: TimelineItem[] = [...rest.slice(0, at), marker, ...rest.slice(at)];
      return { ...state, items, streaming: true };
    }

    case 'done':
      return { ...state, streaming: false, activity: null, activeTool: null, progress: null };

    case 'error':
      // Surface as a final assistant text line so the user sees what happened.
      return {
        ...state,
        streaming: false,
        activity: null,
        activeTool: null,
        items: [
          ...state.items,
          {
            kind: 'text',
            role: 'assistant',
            content: `${ev.error}${ev.reason ? ` (${ev.reason})` : ''}`,
            ...(ev.error_code ? { errorCode: ev.error_code } : {}),
          },
        ],
      };

    default:
      return state;
  }
}

/**
 * Hydrate a fresh timeline from persisted messages. Each assistant message
 * may carry an `aoa_tool_calls` array in `message_metadata` (written by
 * the AoA stream route on ai_complete) — when present, we inline the
 * tool-call blocks BEFORE the assistant text so the rehydrated transcript
 * looks like what the user saw during streaming.
 *
 * Skill banners ride along in `aoa_skill_activations`, each carrying the
 * number of tool calls that had already streamed when it fired — so the label
 * lands back at its original position among that turn's items rather than
 * being lumped at one end.
 */
export function hydrateFromMessages(
  messages: Array<{
    sender_type: string;
    message_content: string;
    message_metadata?: {
      aoa_tool_calls?: Array<{
        tool: string;
        args: Record<string, unknown>;
        result: string | null;
        invocation_id: string;
        /** Set by the AoA stream route when the tool was a propose_* —
         *  used here to look up the proposal's persisted status from the
         *  `proposalsById` map and re-render the resolved card. Without
         *  this, refreshing after a successful Accept would leave only
         *  the tool-call block visible. */
        proposal_id?: string;
      }>;
      /** Skill banners this turn fired, written by the AoA stream route on
       *  ai_complete alongside `aoa_tool_calls`. `after_tool_calls` is how
       *  many tool CALLS had already streamed when the badge appeared, which
       *  is what lets us splice the label back at its original position
       *  instead of stacking every badge at the top of the turn. */
      aoa_skill_activations?: Array<{
        skill_name: string;
        after_tool_calls?: number;
      }>;
      /** The turn's web searches, shown like its tool calls but kept apart
       *  from `aoa_tool_calls` (those are replayed to the model; a search
       *  shown here must not be). `after_tool_calls` places each one, as for
       *  a skill banner. */
      aoa_search_calls?: Array<{
        tool: string;
        args: Record<string, unknown>;
        result: string | null;
        invocation_id: string;
        after_tool_calls?: number;
      }>;
      /** Synthetic resolution-of-proposal message written by the
       *  /continue-from-proposal endpoint. Tagged so we can filter it
       *  out of the visible chat when rehydrating an old conversation —
       *  the user must NOT see "[system signal — not from the user]"
       *  rendered as if they had typed it. */
      is_continuation_marker?: boolean;
      /** Written by ConversationService._persist_interrupted_turn when the
       *  user stopped the turn — the row holds the partial text. Rendered
       *  with the same "Response stopped" note the live stop shows, so a
       *  refresh doesn't silently promote a half-answer to a full one. */
      interrupted?: boolean;
      /** The output cap cut the answer off after its automatic
       *  continuations (AgentConversation). Rendered as a note under it. */
      answer_cut_off?: boolean;
      answer_cut_off_reason?: string;
    };
    /** Persisted user-upload attachments — present on user messages
     *  that carried file uploads. Carried through to the hydrated
     *  TextItem so the post-refresh view matches the live UX. */
    attachments?: AoAAttachment[];
  }>,
  /** Optional map of proposals keyed by proposal_id. When provided,
   *  any tool_call with a matching proposal_id gets a ProposalCard
   *  rendered after it, with the proposal's CURRENT status from the DB
   *  (approved / edited / rejected / pending). Caller fetches this map
   *  via aoaApi.listProposals(conversationId).
   *
   *  Defensive note: if a proposal exists for this conversation but no
   *  tool_call carries its id (e.g. older message metadata, or a turn
   *  whose metadata-persist step failed), it is appended at the END of
   *  the timeline as a standalone marker so the user still sees the
   *  pending / resolved card on the chat page when they return. */
  proposalsById?: Record<string, AoAProposal>,
): TimelineState {
  const items: TimelineItem[] = [];
  // Track which proposal_ids we've already placed inline, so the
  // unmatched-tail pass below doesn't duplicate them.
  const placedProposalIds = new Set<string>();
  // Index positions where a propose_* tool_call was emitted but we
  // couldn't pair it to a proposal_id directly (older metadata, or a
  // turn whose backend persist step missed the id). Pass 2 below
  // tries to fill these slots by tool_name + chronological order
  // before falling back to the tail bucket.
  const pendingSlots: Array<{ insertAfterIdx: number; toolName: string }> = [];
  // A skill stays loaded for the rest of the session, so the backend only
  // badges its FIRST activation. Dedupe across the whole conversation to match
  // — the live reducer scans the entire timeline for the same reason.
  const seenSkills = new Set<string>();
  for (const m of messages) {
    // Continuation markers are stored as user-role messages but are
    // internal signals to the model, not user input — drop them from
    // the visible chat (the assistant's subsequent reply IS what the
    // user actually wanted to see).
    if (m.message_metadata?.is_continuation_marker) continue;
    if (m.sender_type === 'system') {
      // A compaction summary row. Only the live one is drawn — a later round
      // folded the earlier markers too (`compacted_into` is set on them).
      const meta = (m.message_metadata ?? {}) as Record<string, unknown>;
      if (meta.compaction && !(m as { compacted_into?: string | null }).compacted_into) {
        items.push({
          kind: 'compaction',
          messageId: (m as { message_id?: string }).message_id,
          summary: m.message_content,
          summarizedCount: Number(meta.summarized_count ?? 0),
          round: Number(meta.round ?? 1),
        });
      }
      continue;
    }
    if (m.sender_type === 'assistant') {
      // Order MATTERS for rehydration UX. In practice AoA's turn pattern
      // is read-tools-FIRST, prose-LAST (read_eval_conversation /
      // read_agent / browse_… runs to gather context, then the
      // assistant answers based on what it read). User flagged
      // 2026-06-12 that the LAST message in a reloaded conversation
      // was showing tool calls AFTER the assistant text — that's
      // backwards: live they happened BEFORE the text. So we push
      // tool_calls (and their proposal cards) first, then the
      // assistant text. Persisted state doesn't carry per-event
      // timestamps, so we cannot recover exact interleaving for the
      // rarer "intro → tool → summary" pattern, but tools-then-text
      // matches what the user actually saw live for the read-then-
      // answer pattern that dominates AoA turns.
      const calls = m.message_metadata?.aoa_tool_calls ?? [];
      // Bucket this turn's skill banners and web searches by the tool-call
      // count they were recorded at, so each one can be flushed into the
      // exact slot it occupied live: bucket 0 comes before the first tool
      // call, bucket N after the Nth (banners first, then searches, in the
      // order they ran). Out-of-range values are clamped rather than dropped —
      // an item in roughly the right place beats no item at all.
      const slotted = new Map<number, TimelineItem[]>();
      const put = (after: number | undefined, item: TimelineItem) => {
        const pos = Math.max(0, Math.min(calls.length, typeof after === 'number' ? after : 0));
        const bucket = slotted.get(pos);
        if (bucket) bucket.push(item);
        else slotted.set(pos, [item]);
      };
      const toolBlock = (c: { tool: string; args: Record<string, unknown>; result: string | null; invocation_id: string }): ToolCallItem => ({
        kind: 'tool_call',
        invocationId: c.invocation_id,
        tool: c.tool,
        args: c.args || {},
        status: 'done',
        result: c.result ?? undefined,
      });
      for (const a of m.message_metadata?.aoa_skill_activations ?? []) {
        const label = (a?.skill_name || '').trim();
        if (!label || looksLikeToolName(label) || seenSkills.has(label)) continue;
        seenSkills.add(label);
        put(a.after_tool_calls, { kind: 'activation', label });
      }
      for (const sc of m.message_metadata?.aoa_search_calls ?? []) put(sc.after_tool_calls, toolBlock(sc));
      const flushSlot = (pos: number) => {
        for (const item of slotted.get(pos) ?? []) items.push(item);
      };
      flushSlot(0);
      for (let ci = 0; ci < calls.length; ci += 1) {
        const c = calls[ci];
        items.push(toolBlock(c));
        // After the tool_call block, also push the proposal card it
        // emitted — so a refreshed conversation shows the same Accept /
        // Edit / Reject card the user saw live (in whatever final state
        // they left it). If the tool_call is mid-flight when the user
        // refreshed (rare), the card stays in 'pending' and the user
        // can still resolve it.
        if (c.proposal_id && proposalsById) {
          const p = proposalsById[c.proposal_id];
          if (p) {
            items.push({
              kind: 'proposal',
              proposal: p,
              resolved: p.status !== 'pending' && p.resolved_at
                ? { status: p.status, resolvedAt: p.resolved_at }
                : undefined,
            });
            placedProposalIds.add(p.proposal_id);
          } else if (typeof c.tool === 'string' && c.tool.startsWith('propose_')) {
            // We have an id but no matching proposal in the map (very
            // rare — proposal was filtered out by status / paging).
            // Treat it like a missing-id propose so the by-name pass
            // below can try its luck.
            pendingSlots.push({ insertAfterIdx: items.length - 1, toolName: c.tool });
          }
        } else if (proposalsById && typeof c.tool === 'string' && c.tool.startsWith('propose_')) {
          // No proposal_id on this propose_* tool_call (older metadata
          // shape OR the backend persist step couldn't unwrap the
          // ADK-wrapped tool result). Remember the slot — pass 2 will
          // try to fill it by matching tool_name to an unmatched
          // proposal in chronological order.
          pendingSlots.push({ insertAfterIdx: items.length - 1, toolName: c.tool });
        }
        // Any banner that fired after THIS call goes in now — after the card
        // it may have preceded, exactly as it streamed.
        flushSlot(ci + 1);
      }
      // Assistant prose comes LAST — after the tools that gathered
      // context. A propose-only turn (just a tool call + card, no
      // prose — which the persona explicitly allows) persists with
      // empty message_content; the empty-text branch is correctly
      // skipped and the tool_call + proposal card above are the full
      // visible response in that case.
      if ((m.message_content && m.message_content.trim()) || m.message_metadata?.answer_cut_off) {
        items.push({
          kind: 'text',
          role: 'assistant',
          content: m.message_content,
          ...answerLabelOf(m),
          interrupted: Boolean(m.message_metadata?.interrupted),
          cutOff: Boolean(m.message_metadata?.answer_cut_off),
          cutOffReason: m.message_metadata?.answer_cut_off_reason,
          limitReached: Boolean((m.message_metadata as any)?.answer_limit_reached),
          webSources: (m.message_metadata as any)?.web_sources as WebSource[] | undefined,
          messageId: (m as { message_id?: string }).message_id,
          storedLength: (m.message_content || '').length,
          // Files the meta-agent WROTE on this turn, bound to the assistant
          // message at turn end. Carried through so a reopened conversation
          // still offers them where they were produced - the same chip the
          // regular chat puts under an agent's reply.
          attachments: (m as any)?.attachments ?? undefined,
        });
      }
    } else {
      items.push({
        kind: 'text',
        role: 'user',
        content: m.message_content,
        // Hydrate attachments from the persisted message — same shape
        // the live user_message SSE event uses, so the visual is
        // identical whether the message just streamed or was loaded
        // from history.
        attachments: (m as any)?.attachments ?? undefined,
        messageId: (m as { message_id?: string }).message_id,
      });
    }
  }

  // Pass 2 — fill the slots we marked above by matching tool_name to
  // unmatched proposals in chronological order. Walks slots in document
  // order, picks the oldest still-unmatched proposal with the same
  // tool_name, and splices it RIGHT after the tool_call. Tracks an
  // insertOffset because each splice shifts subsequent slot indices.
  if (proposalsById && pendingSlots.length > 0) {
    const remaining = Object.values(proposalsById)
      .filter((p) => !placedProposalIds.has(p.proposal_id))
      .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    let insertOffset = 0;
    for (const slot of pendingSlots) {
      const idx = remaining.findIndex((p) => p.tool_name === slot.toolName);
      if (idx < 0) continue;
      const [p] = remaining.splice(idx, 1);
      items.splice(slot.insertAfterIdx + 1 + insertOffset, 0, {
        kind: 'proposal',
        proposal: p,
        resolved: p.status !== 'pending' && p.resolved_at
          ? { status: p.status, resolvedAt: p.resolved_at }
          : undefined,
      });
      insertOffset += 1;
      placedProposalIds.add(p.proposal_id);
    }
  }

  // Defensive tail pass: surface any proposal that the API returned for
  // this conversation but that we couldn't pin to a specific tool_call
  // (e.g. unknown tool name, or more proposals than tool_calls). The
  // user always sees the marker in the chat — that's more important
  // than its exact inline placement.
  if (proposalsById) {
    const unmatched = Object.values(proposalsById)
      .filter((p) => !placedProposalIds.has(p.proposal_id))
      // Stable order by creation time so re-renders are deterministic.
      .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    for (const p of unmatched) {
      items.push({
        kind: 'proposal',
        proposal: p,
        resolved: p.status !== 'pending' && p.resolved_at
          ? { status: p.status, resolvedAt: p.resolved_at }
          : undefined,
      });
    }
  }

  return { streaming: false, activity: null, activeTool: null, items };
}
